import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { get, ref } from "firebase/database";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextFieldMUI from "@mui/material/TextField";
import ModalBasicLayout from "../Modal/ModalBasicLayout";
import ContainerModalActions from "../Containers/ContainerModalActions";
import Select, { SELECT_MENU_ABOVE_MODAL_PROPS } from "../Form/Select";
import {
  MODAL_RESIZE_CUSTOM_TITLE,
  MODAL_RESIZE_CUSTOM_DESCRIPTION,
  MODAL_RESIZE_CUSTOM_DEFAULT_SIZE_LABEL,
  MODAL_CANVA_IMAGE_CONFIG_WIDTH_LABEL,
  MODAL_CANVA_IMAGE_CONFIG_HEIGHT_LABEL,
  MODAL_CANVA_IMAGE_CONFIG_CANCEL_BUTTON,
  BUTTON_SAVE,
  ERROR_RESIZE_DIMENSION_INVALID,
  ERROR_RESIZE_DIMENSION_MAX_EDGE,
  ERROR_RESIZE_DIMENSION_NOT_MULTIPLE_PREFIX,
  ERROR_RESIZE_DIMENSION_OR,
  ERROR_RESIZE_ASPECT_RATIO_EXCEEDED_PREFIX,
  ERROR_RESIZE_ASPECT_RATIO_CHOOSE_OPTION,
  ERROR_RESIZE_ASPECT_RATIO_ADJUST_WIDTH_PREFIX,
  ERROR_RESIZE_ASPECT_RATIO_KEEP_HEIGHT_SUFFIX,
  ERROR_RESIZE_ASPECT_RATIO_ADJUST_HEIGHT_PREFIX,
  ERROR_RESIZE_ASPECT_RATIO_KEEP_WIDTH_SUFFIX,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { db } from "../../lib/firebase";
import { useSelectorApp } from "../../lib/redux";
import { textFieldInputSx } from "../../styles";
import {
  RESIZE_MAX_EDGE,
  areResizeDimensionsReady,
  validateResizeDimension,
  validateResizeDimensionsGlobal,
} from "../../utils/resizeCustomDimensions";

const normalizeResizeDefaultOptions = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.filter(
    (item) =>
      item &&
      typeof item.label === "string" &&
      item.label.trim() !== "" &&
      Number.isFinite(Number(item.width)) &&
      Number.isFinite(Number(item.height))
  );
};

const suggestionButtonSx = {
  p: 0,
  border: "none",
  background: "none",
  font: "inherit",
  color: "inherit",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "text.secondary",
  },
};

const DimensionSuggestion = ({ value, onSelect }) => (
  <Typography
    component="button"
    type="button"
    variant="caption"
    onClick={() => onSelect(value)}
    sx={suggestionButtonSx}
  >
    {value}px
  </Typography>
);

const DimensionFieldHint = ({ validation, onSelectSuggestion, t }) => {
  if (!validation || validation.valid || validation.reason === "empty") {
    return null;
  }

  if (validation.reason === "invalid") {
    return (
      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
        {t(ERROR_RESIZE_DIMENSION_INVALID)}
      </Typography>
    );
  }

  if (validation.reason === "maxEdge") {
    const suggestion = validation.suggestions?.[0] ?? RESIZE_MAX_EDGE;

    return (
      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
        {t(ERROR_RESIZE_DIMENSION_MAX_EDGE, { max: RESIZE_MAX_EDGE })}{" "}
        <DimensionSuggestion value={suggestion} onSelect={onSelectSuggestion} />
      </Typography>
    );
  }

  if (validation.reason === "notMultipleOf16") {
    const suggestions = validation.suggestions ?? [];

    if (suggestions.length === 0) {
      return (
        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
          {t(ERROR_RESIZE_DIMENSION_NOT_MULTIPLE_PREFIX)}{" "}
          <DimensionSuggestion
            value={RESIZE_MAX_EDGE}
            onSelect={onSelectSuggestion}
          />
        </Typography>
      );
    }

    return (
      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
        {t(ERROR_RESIZE_DIMENSION_NOT_MULTIPLE_PREFIX)}{" "}
        {suggestions.map((suggestion, index) => (
          <Box component="span" key={suggestion}>
            {index > 0 && ` ${t(ERROR_RESIZE_DIMENSION_OR)} `}
            <DimensionSuggestion
              value={suggestion}
              onSelect={onSelectSuggestion}
            />
          </Box>
        ))}
      </Typography>
    );
  }

  return null;
};

const ResizeDimensionField = ({
  control,
  name,
  label,
  validation,
  onSelectSuggestion,
  t,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
    <Typography variant="body1" sx={{ mb: 1 }}>
      {label}
    </Typography>
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <TextFieldMUI
          {...field}
          type="number"
          fullWidth
          variant="filled"
          hiddenLabel
          error={
            !!validation && !validation.valid && validation.reason !== "empty"
          }
          inputProps={{ min: 1 }}
          sx={textFieldInputSx}
        />
      )}
    />
    <DimensionFieldHint
      validation={validation}
      onSelectSuggestion={onSelectSuggestion}
      t={t}
    />
  </Box>
);

const GlobalResizeValidation = ({
  globalValidation,
  onSelectWidthSuggestion,
  onSelectHeightSuggestion,
  t,
}) => {
  if (!globalValidation || globalValidation.valid) {
    return null;
  }

  if (globalValidation.reason === "aspectRatioExceeded") {
    const adjustWidthSuggestion = globalValidation.suggestions?.find(
      (suggestion) => suggestion.field === "width"
    );
    const adjustHeightSuggestion = globalValidation.suggestions?.find(
      (suggestion) => suggestion.field === "height"
    );
    const hasSuggestions = Boolean(
      adjustWidthSuggestion || adjustHeightSuggestion
    );

    return (
      <Box sx={{ mb: 2, color: "error.main" }}>
        <Typography
          variant="caption"
          color="error"
          component="p"
          sx={{ m: 0, mb: hasSuggestions ? 0.5 : 0 }}
        >
          {t(ERROR_RESIZE_ASPECT_RATIO_EXCEEDED_PREFIX)}
          {hasSuggestions && <> {t(ERROR_RESIZE_ASPECT_RATIO_CHOOSE_OPTION)}</>}
        </Typography>
        {hasSuggestions && (
          <Box
            component="ul"
            sx={{
              m: 0,
              p: 0,
              pl: 2.5,
              listStyleType: "disc",
              listStylePosition: "outside",
              fontSize: (theme) => theme.typography.caption.fontSize,
              lineHeight: (theme) => theme.typography.caption.lineHeight,
            }}
          >
            {adjustWidthSuggestion && (
              <Box
                component="li"
                sx={{
                  display: "list-item",
                  mb: 0.5,
                  color: "error.main",
                  "&::marker": {
                    color: "error.main",
                  },
                }}
              >
                {t(ERROR_RESIZE_ASPECT_RATIO_ADJUST_WIDTH_PREFIX)}{" "}
                <DimensionSuggestion
                  value={adjustWidthSuggestion.value}
                  onSelect={onSelectWidthSuggestion}
                />{" "}
                {t(ERROR_RESIZE_ASPECT_RATIO_KEEP_HEIGHT_SUFFIX)}
              </Box>
            )}
            {adjustHeightSuggestion && (
              <Box
                component="li"
                sx={{
                  display: "list-item",
                  color: "error.main",
                  "&::marker": {
                    color: "error.main",
                  },
                }}
              >
                {t(ERROR_RESIZE_ASPECT_RATIO_ADJUST_HEIGHT_PREFIX)}{" "}
                <DimensionSuggestion
                  value={adjustHeightSuggestion.value}
                  onSelect={onSelectHeightSuggestion}
                />{" "}
                {t(ERROR_RESIZE_ASPECT_RATIO_KEEP_WIDTH_SUFFIX)}
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  }

  return null;
};

const ModalResizeCustomImage = ({
  modalOpen = false,
  onCloseModal = () => {},
  onCallbackResize = () => {},
}) => {
  const { t } = useTranslationApp();
  const advertiserID = useSelectorApp(
    (state) => state.auth?.user?.advertiserID
  );
  const [resizeDefaultOptions, setResizeDefaultOptions] = useState([]);
  const isApplyingDefaultRef = useRef(false);

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      defaultSize: "",
      width: "",
      height: "",
    },
    mode: "onChange",
  });

  const defaultSizeValue = watch("defaultSize");
  const widthValue = watch("width");
  const heightValue = watch("height");

  const defaultSizeSelectOptions = useMemo(
    () =>
      resizeDefaultOptions.map((option) => ({
        value: option.label,
        label: option.label,
      })),
    [resizeDefaultOptions]
  );

  const widthValidation = useMemo(
    () => validateResizeDimension(widthValue),
    [widthValue]
  );
  const heightValidation = useMemo(
    () => validateResizeDimension(heightValue),
    [heightValue]
  );
  const globalValidation = useMemo(
    () => validateResizeDimensionsGlobal(widthValue, heightValue),
    [widthValue, heightValue]
  );

  const isResizeEnabled = areResizeDimensionsReady(widthValue, heightValue);

  useEffect(() => {
    if (!modalOpen) return;
    reset({
      defaultSize: "",
      width: "",
      height: "",
    });
  }, [modalOpen, reset]);

  useEffect(() => {
    if (!modalOpen || !advertiserID) {
      setResizeDefaultOptions([]);
      return undefined;
    }

    let cancelled = false;

    const fetchResizeDefaultOptions = async () => {
      try {
        const snapshot = await get(
          ref(db, `resizeDefaultOptions/${advertiserID}`)
        );
        if (cancelled) return;

        setResizeDefaultOptions(normalizeResizeDefaultOptions(snapshot.val()));
      } catch {
        if (!cancelled) {
          setResizeDefaultOptions([]);
        }
      }
    };

    fetchResizeDefaultOptions();

    return () => {
      cancelled = true;
    };
  }, [modalOpen, advertiserID]);

  useEffect(() => {
    if (isApplyingDefaultRef.current || !defaultSizeValue) {
      return;
    }

    const selectedOption = resizeDefaultOptions.find(
      (option) => option.label === defaultSizeValue
    );

    if (!selectedOption) {
      setValue("defaultSize", "");
      return;
    }

    const selectedWidth = String(selectedOption.width);
    const selectedHeight = String(selectedOption.height);

    if (
      String(widthValue) !== selectedWidth ||
      String(heightValue) !== selectedHeight
    ) {
      setValue("defaultSize", "");
    }
  }, [
    defaultSizeValue,
    heightValue,
    resizeDefaultOptions,
    setValue,
    widthValue,
  ]);

  const onSubmitResize = handleSubmit(({ width, height }) => {
    const result = validateResizeDimensionsGlobal(width, height);
    if (!result?.valid) return;

    onCallbackResize({
      width: result.width,
      height: result.height,
    });
  });

  const onSelectDefaultSize = (label) => {
    const selectedOption = resizeDefaultOptions.find(
      (option) => option.label === label
    );

    if (!selectedOption) {
      return;
    }

    isApplyingDefaultRef.current = true;
    setValue("defaultSize", label);
    setValue("width", String(selectedOption.width), {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue("height", String(selectedOption.height), {
      shouldValidate: true,
      shouldDirty: true,
    });
    isApplyingDefaultRef.current = false;
  };

  const onSelectWidthSuggestion = (value) => {
    setValue("width", String(value), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onSelectHeightSuggestion = (value) => {
    setValue("height", String(value), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      ariaLabel={t(MODAL_RESIZE_CUSTOM_TITLE)}
      title={t(MODAL_RESIZE_CUSTOM_TITLE)}
      description={t(MODAL_RESIZE_CUSTOM_DESCRIPTION)}
    >
      {resizeDefaultOptions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {t(MODAL_RESIZE_CUSTOM_DEFAULT_SIZE_LABEL)}
          </Typography>
          <Select
            control={control}
            name="defaultSize"
            rules={{ required: false }}
            options={defaultSizeSelectOptions}
            fullWidth
            variant="filled"
            MenuProps={SELECT_MENU_ABOVE_MODAL_PROPS}
            actionsChange={onSelectDefaultSize}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <ResizeDimensionField
          control={control}
          name="width"
          label={t(MODAL_CANVA_IMAGE_CONFIG_WIDTH_LABEL)}
          validation={widthValidation}
          onSelectSuggestion={onSelectWidthSuggestion}
          t={t}
        />

        <ResizeDimensionField
          control={control}
          name="height"
          label={t(MODAL_CANVA_IMAGE_CONFIG_HEIGHT_LABEL)}
          validation={heightValidation}
          onSelectSuggestion={onSelectHeightSuggestion}
          t={t}
        />
      </Box>

      <GlobalResizeValidation
        globalValidation={globalValidation}
        onSelectWidthSuggestion={onSelectWidthSuggestion}
        onSelectHeightSuggestion={onSelectHeightSuggestion}
        t={t}
      />

      <ContainerModalActions
        hasPadding={false}
        propsPrimaryButton={{
          showButton: true,
          text: BUTTON_SAVE,
          variant: "contained",
          disabled: !isResizeEnabled,
          onClick: onSubmitResize,
        }}
        propsSecondaryButton={{
          showButton: true,
          text: MODAL_CANVA_IMAGE_CONFIG_CANCEL_BUTTON,
          onClick: onCloseModal,
        }}
      />
    </ModalBasicLayout>
  );
};

export default ModalResizeCustomImage;
